import { useState, type FormEvent } from 'react';
import type { TranscriptCoordinateMapping, TranscriptLocus, TranscriptRegionClassification } from './Transcript';
import { getTranscriptCoordinateMapping } from './transcriptApi';

const REGION: Record<TranscriptRegionClassification, string> = {
  OUTSIDE: 'Outside transcript',
  INTRONIC: 'Intronic',
  FIVE_PRIME_UTR: '5′ UTR',
  CODING: 'Coding',
  THREE_PRIME_UTR: '3′ UTR',
  NON_CODING_EXON: 'Non-coding exon',
  EXONIC_UNCLASSIFIED: 'Exonic, unclassified'
};

interface Props {
  readonly transcriptId: string;
  readonly loci: readonly TranscriptLocus[];
}

type State =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly result: TranscriptCoordinateMapping }
  | { readonly status: 'error'; readonly message: string };

export function TranscriptCoordinateInspector({ transcriptId, loci }: Props) {
  const [locusOrdinal, setLocusOrdinal] = useState(1);
  const [genomicPosition, setGenomicPosition] = useState('');
  const [state, setState] = useState<State>({ status: 'idle' });

  async function inspect(event: FormEvent) {
    event.preventDefault();
    const position = Number(genomicPosition);
    if (!Number.isSafeInteger(position) || position < 0) {
      setState({ status: 'error', message: 'Enter a non-negative integer genomic position.' });
      return;
    }
    setState({ status: 'loading' });
    try {
      const result = await getTranscriptCoordinateMapping(transcriptId, locusOrdinal, position);
      setState({ status: 'ready', result });
    } catch (error) {
      setState({
        status: 'error',
        message: error instanceof Error ? error.message : 'Unable to inspect this coordinate.'
      });
    }
  }

  return (
    <section className="transcript-coordinate-inspector">
      <h2>Coordinate inspector</h2>
      <form onSubmit={inspect}>
        <label>
          Locus
          <select value={locusOrdinal} onChange={(event) => setLocusOrdinal(Number(event.target.value))}>
            {loci.map((locus, index) => (
              <option
                key={`${locus.assembly.identifier}:${locus.referenceSequence.identifier}:${index}`}
                value={index + 1}
              >
                {index + 1}. {locus.assembly.displayName} / {locus.referenceSequence.displayName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Genomic position
          <input
            inputMode="numeric"
            value={genomicPosition}
            onChange={(event) => setGenomicPosition(event.target.value)}
          />
        </label>
        <button type="submit" disabled={state.status === 'loading'}>
          {state.status === 'loading' ? 'Inspecting…' : 'Inspect'}
        </button>
      </form>
      {state.status === 'error' ? <p role="alert">{state.message}</p> : null}
      {state.status === 'ready' ? (
        <dl className="transcript-facts coordinate-results">
          <div>
            <dt>Genomic position</dt>
            <dd>{state.result.genomicPosition}</dd>
          </div>
          <div>
            <dt>Transcript position</dt>
            <dd>{state.result.transcriptPosition ?? 'Not mapped'}</dd>
          </div>
          <div>
            <dt>CDS position</dt>
            <dd>{state.result.cdsPosition ?? 'Not mapped'}</dd>
          </div>
          <div>
            <dt>Region</dt>
            <dd>{REGION[state.result.region]}</dd>
          </div>
        </dl>
      ) : null}
      <p className="coordinate-note">
        Positions are zero-based. Genomic intervals elsewhere use half-open [start, end) notation.
      </p>
    </section>
  );
}
